import type { ThreeEvent } from '@react-three/fiber'
import { useMemo } from 'react'
import * as THREE from 'three'
import {
  CATALOGO_VIDROS,
  type EstadoManejoVidro,
  type IdVidro,
  type ProtecoesManejo,
} from '../../dominio/didatico/manejoVidros'
import { ADULTO_MASCULINO } from '../../dominio/didatico/perfisVitima'
import { POSE_SENTADA } from '../../dominio/didatico/rigVitima'
import { useMaquete } from '../componentes/contextoMaquete'
import { VeiculoDidatico } from '../veiculo/VeiculoDidatico'
import { VitimaProcedural } from '../vitima/VitimaProcedural'
import { CobertorMaleavel3D } from './CobertorMaleavel3D'
import { DetritosVidroPersistentes3D } from './DetritosVidroPersistentes3D'
import { LagrimaProtecao3D } from './LagrimaProtecao3D'
import { regioesVidroDoPerfil } from './regioesVidro'
import { SistemaParticulasVidro } from './SistemaParticulasVidro'
import { calcularPoseLagrima } from '../oficina/posicionamentoLagrima'

export interface PropsCenaManejoVidros {
  vidroSelecionado: IdVidro
  estadosVidros: Record<IdVidro, EstadoManejoVidro>
  protecoes: ProtecoesManejo
  temInsulfilm?: boolean
  detritosNaVitima?: boolean
  detritosNasProtecoes?: boolean
  peliculaNoSolo?: boolean
  aoSelecionarVidro: (id: IdVidro) => void
  animacaoAtiva: {
    tipo: 'poLaminado' | 'estilhacosTemperado'
    origem: [number, number, number]
  } | null
  aoFinalizarAnimacao: () => void
}

export function CenaManejoVidros({
  vidroSelecionado,
  estadosVidros,
  protecoes,
  temInsulfilm = false,
  detritosNaVitima = false,
  detritosNasProtecoes = false,
  peliculaNoSolo = false,
  aoSelecionarVidro,
  animacaoAtiva,
  aoFinalizarAnimacao,
}: PropsCenaManejoVidros) {
  const { perfil } = useMaquete()

  // Âncora do motorista
  const ancoraMotorista = perfil.ancoras.find((a) => a.id === 'motorista') ?? perfil.ancoras[0]!
  const colocacaoVitima = useMemo(
    () => ({
      posicaoPelve: ancoraMotorista.posicao,
      guinadaRad: ancoraMotorista.rotacao.y,
    }),
    [ancoraMotorista],
  )

  // Posições espaciais didáticas de cada vidro para clique e interação
  const regioesVidro = regioesVidroDoPerfil(perfil)
  const meiaLargura = perfil.largura / 2
  const zDiant = (perfil.colunaA.zBase + perfil.colunaB.z) / 2

  const posVitima: [number, number, number] = useMemo(
    () => [
      colocacaoVitima.posicaoPelve.x,
      colocacaoVitima.posicaoPelve.y + 0.38,
      colocacaoVitima.posicaoPelve.z,
    ],
    [colocacaoVitima],
  )

  // Posição interposta da Lágrima protegendo a vítima em relação ao vidro ativo
  const poseLagrima = useMemo(() => {
    const posVidro = regioesVidro[vidroSelecionado].posicao
    const lado = posVidro[0] > 0 ? 'direito' : 'esquerdo'
    return calcularPoseLagrima({
      posicaoVitima: posVitima,
      posicaoPerigo: posVidro,
      lado,
      fatorInterposto: 0.52,
    })
  }, [posVitima, regioesVidro, vidroSelecionado])

  const mostrarLagrima =
    protecoes.lagrimaProtecao ||
    animacaoAtiva !== null ||
    estadosVidros[vidroSelecionado] === 'quebrado'

  return (
    <group>
      {/* Veículo com habitáculo e estrutura */}
      <VeiculoDidatico perfil={perfil} />

      {/* Vítima sentada no banco do motorista */}
      <VitimaProcedural
        colocacao={colocacaoVitima}
        pose={POSE_SENTADA}
        proporcoes={ADULTO_MASCULINO}
      />

      {/* Detritos persistentes sobre a vítima desprotegida */}
      {detritosNaVitima && (
        <DetritosVidroPersistentes3D
          tipo={CATALOGO_VIDROS[vidroSelecionado].tipo === 'laminado' ? 'poLaminado' : 'estilhacosTemperado'}
          local="vitima"
          posicaoBase={[colocacaoVitima.posicaoPelve.x, colocacaoVitima.posicaoPelve.y, colocacaoVitima.posicaoPelve.z]}
        />
      )}

      {/* Cobertor Maleável Semi-Transparente sobre a vítima */}
      <CobertorMaleavel3D
        visivel={protecoes.cobertorMaleavel}
        posicao={[colocacaoVitima.posicaoPelve.x, colocacaoVitima.posicaoPelve.y, colocacaoVitima.posicaoPelve.z]}
      />

      {/* Detritos persistentes sobre o cobertor maleável */}
      {detritosNasProtecoes && protecoes.cobertorMaleavel && (
        <DetritosVidroPersistentes3D
          tipo={CATALOGO_VIDROS[vidroSelecionado].tipo === 'laminado' ? 'poLaminado' : 'estilhacosTemperado'}
          local="cobertor"
          posicaoBase={[colocacaoVitima.posicaoPelve.x, colocacaoVitima.posicaoPelve.y + 0.08, colocacaoVitima.posicaoPelve.z]}
        />
      )}

      {/* Escudo rígido 'Lágrima' posicionado entre a vítima e o vidro selecionado */}
      <LagrimaProtecao3D
        visivel={mostrarLagrima && poseLagrima.visivel}
        posicao={poseLagrima.posicao}
        rotacao={poseLagrima.rotacao}
        escala={poseLagrima.escala}
        destaque={animacaoAtiva !== null}
      />

      {/* Detritos retidos sobre o escudo Lágrima */}
      {detritosNasProtecoes && mostrarLagrima && (
        <DetritosVidroPersistentes3D
          tipo="estilhacosTemperado"
          local="lagrima"
          posicaoBase={poseLagrima.posicao}
        />
      )}

      {/* Placa de insulfilm no solo externo após remoção correta */}
      {peliculaNoSolo && (
        <DetritosVidroPersistentes3D
          tipo="estilhacosTemperado"
          local="solo"
          posicaoBase={[-meiaLargura - 0.55, 0, zDiant]}
        />
      )}

      {/* Malhas interativas de clique para cada vidro do veículo */}
      {(Object.keys(regioesVidro) as IdVidro[]).map((id) => {
        const reg = regioesVidro[id]
        const def = CATALOGO_VIDROS[id]
        const selecionado = id === vidroSelecionado
        const estado = estadosVidros[id] ?? 'intacto'

        const aoClicar = (e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation()
          aoSelecionarVidro(id)
        }

        const temPel = temInsulfilm && def.tipo === 'temperado'

        const corMaterial =
          estado === 'craqueladoComPelicula'
            ? '#1e293b'
            : estado === 'cortado' || estado === 'quebrado' || estado === 'removido'
              ? '#334155'
              : selecionado
                ? '#38bdf8'
                : temPel
                  ? '#0f172a'
                  : def.tipo === 'laminado'
                    ? '#7dd3fc'
                    : '#93c5fd'

        const opacidade =
          estado === 'craqueladoComPelicula'
            ? 0.85
            : estado === 'cortado' || estado === 'quebrado' || estado === 'removido'
              ? 0.08
              : selecionado
                ? 0.78
                : temPel
                  ? 0.65
                  : 0.45

        return (
          <group key={id} position={reg.posicao} rotation={reg.rotacao} onClick={aoClicar}>
            <mesh>
              <boxGeometry args={reg.dimensoes} />
              <meshPhysicalMaterial
                color={corMaterial}
                transparent
                opacity={opacidade}
                roughness={estado === 'craqueladoComPelicula' ? 0.6 : 0.2}
                metalness={estado === 'craqueladoComPelicula' ? 0.3 : 0.1}
                transmission={estado === 'craqueladoComPelicula' ? 0.1 : 0.3}
                side={THREE.DoubleSide}
              />
            </mesh>

            {/* Vidro craquelado na película (malha de trincas coesas) */}
            {estado === 'craqueladoComPelicula' && (
              <mesh position={[0, 0, 0.022]}>
                <planeGeometry args={[reg.dimensoes[0] * 0.96, reg.dimensoes[1] * 0.96, 16, 12]} />
                <meshStandardMaterial
                  color="#94a3b8"
                  wireframe
                  transparent
                  opacity={0.8}
                  side={THREE.DoubleSide}
                />
              </mesh>
            )}

            {/* Fita contact aplicada em xadrez */}
            {protecoes.fitaContact && def.tipo === 'temperado' && estado === 'intacto' && (
              <mesh position={[0, 0, 0.025]}>
                <planeGeometry args={[reg.dimensoes[0] * 0.95, reg.dimensoes[1] * 0.95]} />
                <meshStandardMaterial
                  color="#fef08a"
                  transparent
                  opacity={0.5}
                  wireframe
                  wireframeLinewidth={3}
                  side={THREE.DoubleSide}
                />
              </mesh>
            )}

          </group>
        )
      })}

      {/* Sistema de partículas (pó de sílica no corte laminado ou estilhaços no temperado) */}
      {animacaoAtiva && (
        <SistemaParticulasVidro
          ativo={true}
          tipo={animacaoAtiva.tipo}
          origem={animacaoAtiva.origem}
          onFinalizar={aoFinalizarAnimacao}
        />
      )}
    </group>
  )
}
